import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import StatsCounter from "@/components/landing/StatsCounter";
import Benefits from "@/components/landing/Benefits";
import Footer from "@/components/landing/Footer";
import CookieConsent from "@/components/landing/CookieConsent";

// Heavy below-the-fold sections bundled into a single deferred chunk.
const LandingBelowFold = lazy(() => import("@/components/landing/LandingBelowFold"));

const Index = () => {
  const location = useLocation();
  const [showBelowFold, setShowBelowFold] = useState(false);

  // Defer below-the-fold rendering until the browser is idle so the LCP /
  // first paint isn't competing with parsing/executing 14+ heavy sections.
  useEffect(() => {
    if (showBelowFold) return;

    // If user lands with a hash anchor, render immediately so scroll works.
    if (location.hash && !location.hash.includes("access_token")) {
      setShowBelowFold(true);
      return;
    }

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const trigger = () => setShowBelowFold(true);
    const schedule = () => {
      if (typeof w.requestIdleCallback === "function") {
        w.requestIdleCallback(trigger, { timeout: 1500 });
      } else {
        setTimeout(trigger, 200);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
      return () => window.removeEventListener("load", schedule);
    }
  }, [location.hash, showBelowFold]);

  useLayoutEffect(() => {
    if (location.hash && !location.hash.includes("access_token") && !location.hash.includes("token_type")) {
      const tryScroll = () => {
        try {
          const el = document.querySelector(location.hash);
          if (el) {
            el.scrollIntoView({ behavior: "instant" as ScrollBehavior });
            return true;
          }
        } catch {
          // Invalid selector
        }
        return false;
      };
      if (!tryScroll()) {
        requestAnimationFrame(() => tryScroll());
      }
    }
  }, [location.hash, showBelowFold]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Header />
      <main>
        <Hero />
        <StatsCounter />
        <section aria-label="Benefícios">
          <Benefits />
        </section>
        {showBelowFold && (
          <Suspense fallback={<div className="min-h-[80vh]" aria-hidden />}>
            <LandingBelowFold />
          </Suspense>
        )}
      </main>
      {showBelowFold && <Footer />}
      <CookieConsent />
    </div>
  );
};

export default Index;
